import { LANGUAGE_FALLBACK } from '@/features/common/languageFallback';
import type { LanguageFallback } from '@/features/common/types/LanguageFallback';
import { servedLanguage } from '@/features/publicsite/contentLanguage';
import type { SiteHome } from '@/features/publicsite/types/home/SiteHome';
import type { SiteHomeCard } from '@/features/publicsite/types/home/SiteHomeCard';
import type { Language } from '@/i18n/types';

/**
 * The "Agricultură în prim-plan" section as administered (V108): its texts, its three cards and the
 * hero's image. An address rather than a fetcher, because it is read through usePublicData, which
 * keys its request on the address and the language.
 */
export const SITE_HOME_URL = '/api/v1/public/site/home';

/** The hero's own picture, shown until the server answers and whenever it names no image. */
export const STATIC_HERO_IMAGE = '/img/landing/hero-aerial.jpg';

/** The hero's picture: the administered one when there is one, the static one otherwise. */
export function heroImageSrc(home: Pick<SiteHome, 'heroImageUrl'> | null): string {
  return home?.heroImageUrl ?? STATIC_HERO_IMAGE;
}

/** A card's picture: the administered one when there is one, the card's static one otherwise. */
export function cardImageSrc(
  served: Pick<SiteHomeCard, 'imageUrl'> | undefined,
  staticImage: string,
): string {
  return served?.imageUrl ?? staticImage;
}

/** How the server names a card's text in `untranslatedFields`: 1-based, as the card's position. */
export function cardField(index: number, field: 'label' | 'title' | 'cta'): string {
  return `cards[${index + 1}].${field}`;
}

/**
 * What the section adds to the shell's language notice on an English page. Partial, never whole:
 * the rest of the home page is the dictionary's, in English, so a Romanian section is one part of
 * it — the whole of it when nobody translated the title, some texts when the server names them.
 * Nothing until it has answered: the dictionary's texts shown meanwhile are in the page's language.
 */
export function homeLanguageFallback(
  home: Pick<SiteHome, 'contentLanguage' | 'untranslatedFields'> | null,
  language: Language,
): LanguageFallback {
  if (!home || language !== 'en') return LANGUAGE_FALLBACK.none;
  return servedLanguage(home) === 'ro' || home.untranslatedFields.length > 0
    ? LANGUAGE_FALLBACK.partial
    : LANGUAGE_FALLBACK.none;
}
